import moment from 'moment';
import { sessionStatusEnums } from 'utils';
import { DATE_FORMAT, toPayloadTrial, trialsFromTSummary } from '../../manualSession/constants';

/**
 * Edit Session Data — correcting a COMPLETED session's collected data on the session screen.
 *
 * Edit mode swaps the live target cards for the manual-entry editor rows (`ManualGoalList` /
 * `ManualPinnedTargets`), which hold trials in page state instead of writing per click. Save diffs
 * that draft against what the session held and sends the result as ONE batch to
 * `POST /clinical/sessions/:id/data-corrections`, where it is validated, applied, audited and
 * re-evaluated (mastery / baseline / maintenance) in a single transaction.
 * Backend contract: docs/modules/clinical.md → "Session Data Correction".
 */

export const EDIT_SESSION_DATA_ACTION = 'APPLY_SESSION_DATA_CORRECTIONS';

/** Mirrors `SessionDataCorrectionOp` in the package. */
export const correctionOps = {
    ADD: 'ADD',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
};

/** Why the "Edit Session Data" entry is unavailable, or null when the session can be edited. */
export const editSessionDataBlocker = (session, appt) => {
    if (session?.status !== sessionStatusEnums.COMPLETED) return 'Only a completed session can be edited.';
    if (appt?.locked) return 'This appointment is locked. Unlock it before editing the session data.';
    return null;
};

/**
 * Trials of one target as the editor shows them. The `isAutoZero` row is the "did not occur"
 * placeholder written at completion for a reduction target that collected nothing — it is not a
 * recorded observation, so it is hidden. Recording a trial there sends an ADD, which the backend
 * uses to REPLACE the placeholder.
 */
const editableTrials = (tSummary) =>
    tSummary ? trialsFromTSummary({ ...tSummary, trials: (tSummary.trials || []).filter((t) => !t?.isAutoZero) }) : [];

/** `{ [targetId]: trials }` for every target of the session — the draft's starting point. */
export const sessionTrialsByTarget = (session) => {
    const byTarget = {};
    (session?.goals || []).forEach((g) =>
        (g?.goal?.targets || []).forEach((t) => {
            byTarget[t.id] = editableTrials(t?.tSummary);
        })
    );
    return byTarget;
};

/** The goals of the session, as plain goal objects (`findEntryProblem` reads this shape). */
export const sessionGoalList = (session) => (session?.goals || []).map((g) => g?.goal).filter(Boolean);

// The fields of a payload trial an UPDATE compares. Times are compared in the editor's `HH:mm:ss`.
const COMPARED_FIELDS = ['value', 'label', 'startTime', 'endTime'];

/**
 * The `changes` array for the batch: per target, saved trials missing from the draft → DELETE,
 * saved trials whose fields differ → UPDATE (changed fields only), trials typed in edit mode → ADD.
 *
 * Times are typed as wall-clock `HH:mm:ss` and sent as ISO datetimes on the day the trial was
 * collected (its own start date; the session's start date for a new trial). DURATION / IRT /
 * LATENCY values are derived from the times by the backend, so only the times are sent for them.
 */
export const buildCorrectionChanges = (session, entries, initial) => {
    const sessionDate = moment(session?.startDate).format(DATE_FORMAT);
    const changes = [];

    (session?.goals || []).forEach(({ goal }) => {
        const dataType = goal?.dataType;
        (goal?.targets || []).forEach((target) => {
            const tSummaryId = target?.tSummary?.id;
            const rawById = new Map((target?.tSummary?.trials || []).map((t) => [t?.id, t]));
            const before = initial[target.id] || [];
            const after = entries[target.id] || [];
            const beforeById = new Map(before.map((t) => [t.id, t]));
            const afterIds = new Set(after.map((t) => t.id));

            before.forEach((t) => {
                if (!afterIds.has(t.id)) changes.push({ op: correctionOps.DELETE, tSummaryId, trialId: t.id });
            });

            after.forEach((t) => {
                const saved = beforeById.get(t.id);
                if (!saved) {
                    changes.push({ op: correctionOps.ADD, targetId: target.id, ...toPayloadTrial(dataType, t, sessionDate) });
                    return;
                }

                const raw = rawById.get(t.id);
                const date = raw?.startTime ? moment(raw.startTime).format(DATE_FORMAT) : sessionDate;
                const next = toPayloadTrial(dataType, t, date);
                const patch = {};
                COMPARED_FIELDS.forEach((field) => {
                    if (field in next && t[field] !== saved[field]) patch[field] = next[field];
                });
                if (Object.keys(patch).length) changes.push({ op: correctionOps.UPDATE, tSummaryId, trialId: t.id, ...patch });
            });
        });
    });

    return changes;
};

/** Targets whose status the correction moved, from the batch response. */
export const changedStatuses = (result) => (result?.targets || []).filter((t) => t?.statusBefore !== t?.statusAfter);
